import { ShipmentEntry } from '../types/shipment';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

export const ingredientReceivingApiService = {
  /**
   * Fetch all ingredient receiving entries from the backend
   */
  fetchIngredientReceivingEntries: async (): Promise<ShipmentEntry[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/ingredient-receiving`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch ingredient receiving entries');
      }

      return data.entries;
    } catch (error) {
      console.error('Error fetching ingredient receiving entries from API:', error);
      throw error;
    }
  },

  /**
   * Fetch ingredient receiving entries filtered by supplier ID
   */
  fetchIngredientReceivingBySupplier: async (supplierId: string): Promise<ShipmentEntry[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/ingredient-receiving?supplierId=${supplierId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch ingredient receiving entries by supplier');
      }

      return data.entries;
    } catch (error) {
      console.error('Error fetching ingredient receiving entries by supplier from API:', error);
      throw error;
    }
  },

  /**
   * Fetch a single ingredient receiving entry by ID
   */
  fetchIngredientReceivingEntryById: async (id: string): Promise<ShipmentEntry> => {
    try {
      const response = await fetch(`${API_BASE_URL}/ingredient-receiving/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch ingredient receiving entry');
      }

      return data.entry;
    } catch (error) {
      console.error('Error fetching ingredient receiving entry from API:', error);
      throw error;
    }
  },

  /**
   * Create a new ingredient receiving entry
   * Returns both the created entry and the auto-created trace code
   */
  createIngredientReceivingEntry: async (entry: ShipmentEntry): Promise<{ entry: ShipmentEntry; traceCode: any }> => {
    try {
      console.log('[DEBUG API Service] Sending POST request to create entry...');
      const response = await fetch(`${API_BASE_URL}/ingredient-receiving`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(entry)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('[DEBUG API Service] Response from backend:', data);
      console.log('[DEBUG API Service] data.entry:', data.entry);
      console.log('[DEBUG API Service] data.traceCode:', data.traceCode);

      if (!data.success) {
        throw new Error(data.error || 'Failed to create ingredient receiving entry');
      }

      return { entry: data.entry, traceCode: data.traceCode };
    } catch (error) {
      console.error('[DEBUG API Service] Error creating ingredient receiving entry:', error);
      throw error;
    }
  },

  /**
   * Update an existing ingredient receiving entry
   */
  updateIngredientReceivingEntry: async (entry: ShipmentEntry): Promise<ShipmentEntry> => {
    try {
      const response = await fetch(`${API_BASE_URL}/ingredient-receiving/${entry.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(entry)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update ingredient receiving entry');
      }

      return data.entry;
    } catch (error) {
      console.error('Error updating ingredient receiving entry:', error);
      throw error;
    }
  },

  /**
   * Delete an ingredient receiving entry by ID
   * Returns the IDs of CASCADE deleted trace codes
   */
  deleteIngredientReceivingEntry: async (id: string): Promise<string[]> => {
    try {
      console.log('[DEBUG API Service] Sending DELETE request for entry:', id);
      const response = await fetch(`${API_BASE_URL}/ingredient-receiving/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('[DEBUG API Service] Delete response:', data);
      console.log('[DEBUG API Service] Deleted trace code IDs:', data.deletedTraceCodeIds);

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete ingredient receiving entry');
      }

      return data.deletedTraceCodeIds || [];
    } catch (error) {
      console.error('[DEBUG API Service] Error deleting ingredient receiving entry:', error);
      throw error;
    }
  }
};
